import React from "react";
import styled from "styled-components";
import { Dot } from "recharts";
import { AlertTriangle } from "lucide-react";

import { MEMBERS_PASS_TYPE_MAP } from "constants/businessDashboard";

const TooltipBox = styled.div`
  background: #000000e5;
  padding: 10px 12px;
  border-radius: 6px;
  box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.15);
  min-width: 164px;
`;

const TooltipTitle = styled.p`
  font-size: 0.75rem;
  color: #9ca1ab;
  font-weight: 600;
  margin-bottom: 3px;
`;

const TooltipRow = styled.p`
  font-size: 0.75rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const TooltipLabel = styled.span`
  color: #fff;
`;

const TooltipValue = styled.span<{ $color?: string }>`
  color: ${({ $color }) => $color || "#fff"};
  font-weight: 500;
  display: inline-block;
`;

export const CustomTooltip = ({
  active,
  payload,
  activeTab,
  selectedYear,
}: any) => {
  if (active && payload && payload.length) {
    const mainData = payload.find((p: any) => p.name === "selectedMembers");
    const referenceData = payload.find((p: any) => p.name === "totalMembers");
    const month = payload[0].payload.month.split("-")[1];

    let percentageOfTotal = 0;
    if (mainData && referenceData && referenceData.value !== 0) {
      percentageOfTotal = (mainData.value / referenceData.value) * 100;
    }

    return (
      <TooltipBox>
        <TooltipTitle>{`${selectedYear}.${month}`}</TooltipTitle>
        {mainData && (
          <TooltipRow>
            <TooltipLabel>
              {MEMBERS_PASS_TYPE_MAP[activeTab]?.title || "선택"} 유효 회원수
            </TooltipLabel>
            <TooltipValue>{mainData.value.toLocaleString()}명</TooltipValue>
          </TooltipRow>
        )}
        {activeTab !== "total" && referenceData && (
          <TooltipRow>
            <TooltipLabel>전체 유효 회원수 대비</TooltipLabel>
            <TooltipValue>{percentageOfTotal.toFixed(1)}%</TooltipValue>
          </TooltipRow>
        )}
        <TooltipRow>
          <TooltipLabel>전월 동기간 대비</TooltipLabel>
          <TooltipValue
            $color={
              mainData?.payload.changeRate?.calculable === null
                ? "#6b7280"
                : "#85AEFF"
            }
          >
            {mainData?.payload.changeRate?.calculable === null
              ? "0.0"
              : mainData?.payload.changeRate?.value
                ? mainData?.payload.changeRate?.value?.toFixed(1)
                : mainData?.payload.changeRate?.positive
                  ? "∞"
                  : "-∞"}
            %
          </TooltipValue>
        </TooltipRow>
      </TooltipBox>
    );
  }

  return null;
};

export const LastDot = (props: any) => {
  const { cx, cy, color, index, chartData } = props;
  if (chartData && index === chartData.length - 1) {
    return <Dot cx={cx} cy={cy} r={3} strokeWidth={2} fill={color} />;
  }
  return null;
};

const StyledCard = styled.div`
  border-radius: 0;
  border: none;
  box-shadow: none;
  padding: 0 16px;
  padding-top: 24px;
  gap: 0;
  background: #fff;
`;

const StyledCardHeader = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-left: 0;
  padding-right: 0;
  margin-bottom: 12px;
`;

const StyledCardTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: -0.2px;
  color: #363e52;
  padding: 0;
`;

const StyledCardContent = styled.div`
  padding: 0;
`;

const ScrollableChartWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const ChartInner = styled.div`
  width: 100%;
  height: 200px;
  position: relative;
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
`;

export function ChartSkeleton({ title = "평균 판매가격" }: { title?: string }) {
  return (
    <StyledCard>
      <StyledCardHeader>
        <StyledCardTitle>{title}</StyledCardTitle>
      </StyledCardHeader>
      <StyledCardContent>
        <ScrollableChartWrapper>
          <ChartInner>{/* Y축 그리드 라인 (3줄) */}</ChartInner>
        </ScrollableChartWrapper>
      </StyledCardContent>
    </StyledCard>
  );
}

const ErrorWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 1rem;
  padding: 0 16px;
  padding-top: 24px;
`;

const ErrorIconBox = styled.div`
  width: 36px;
  height: 36px;
  background: #fee2e2;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;
`;

const ErrorTitle = styled.h2`
  font-size: 18px;
  font-weight: bold;
  color: #1f2937;
  margin-bottom: 4px;
`;

const ErrorDesc = styled.p`
  color: #6b7280;
  margin-bottom: 12px;
  font-size: 13px;
`;

export const ChartError = () => (
  <ErrorWrapper>
    <ErrorIconBox>
      <AlertTriangle width={18} height={18} color="#dc2626" strokeWidth={2} />
    </ErrorIconBox>
    <ErrorTitle>데이터를 불러올 수 없습니다</ErrorTitle>
    <ErrorDesc>
      회원 데이터를 불러오는 중 예상치 못한 오류가 발생했습니다.
    </ErrorDesc>
  </ErrorWrapper>
);
